import { jsx as _jsx } from "@emotion/react/jsx-runtime";
/**
 * Copyright 2019, SumUp Ltd.
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { forwardRef } from 'react';
import { resolveCurrencyFormat } from '@sumup/intl';
import NumberFormat from 'react-number-format';
import styled from '../../styles/styled';
import Input from '../Input';
import { formatPlaceholder } from './CurrencyInputService';
const DEFAULT_FORMAT = {
    currencyPosition: 'left',
    currencySymbol: '$',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    decimalDelimiter: '.',
    groupDelimiter: ',',
};
const DUMMY_DELIMITER = '?';
const CurrencyIcon = styled('span') `
  line-height: ${({ theme }) => theme.spacings.mega};
  display: flex;
  align-items: center;
  justify-content: center;
`;
/**
 * CurrencyInput component for forms. Automatically looks up
 * symbols and places the symbol according to the locale. The corresponding
 * service exports a parser for formatting values automatically.
 */
export const CurrencyInput = forwardRef(({ locale, currency, placeholder, ...props }, ref) => {
    const currencyFormat = resolveCurrencyFormat(locale, currency) || DEFAULT_FORMAT;
    const { currencyPosition, currencySymbol, minimumFractionDigits, maximumFractionDigits, decimalDelimiter, groupDelimiter, } = currencyFormat;
    const placeholderString = formatPlaceholder(placeholder, locale, {
        minimumFractionDigits,
        maximumFractionDigits,
    });
    // Allow common decimal signs as well as the one from resolveCurrencyFormat()
    const allowedDecimalSeparators = ['.', ',', decimalDelimiter];
    const renderPrefix = currencyPosition === 'prefix'
        ? (prefixProps) => (_jsx(CurrencyIcon, { ...prefixProps, children: currencySymbol }, void 0))
        : undefined;
    const renderSuffix = currencyPosition === 'suffix'
        ? (suffixProps) => (_jsx(CurrencyIcon, { ...suffixProps, children: currencySymbol }, void 0))
        : undefined;
    return (_jsx(NumberFormat
    // NumberFormat props
    , { 
        // NumberFormat props
        thousandSeparator: groupDelimiter, decimalSeparator: maximumFractionDigits > 0 ? decimalDelimiter : DUMMY_DELIMITER, decimalScale: maximumFractionDigits, customInput: Input, getInputRef: ref, allowedDecimalSeparators: allowedDecimalSeparators, 
        // Circuit input props
        renderPrefix: renderPrefix, renderSuffix: renderSuffix, placeholder: placeholderString, textAlign: "right", type: "text", inputMode: "decimal", ...props }, void 0));
});
CurrencyInput.displayName = 'CurrencyInput';
